/**
 * Initialize Application State After Login
 * 
 * Ensures a fresh, clean state when a new user logs in.
 * This prevents data leakage from previous sessions and ensures
 * proper isolation between different user accounts.
 */

import { store } from '../../store/ReduxStore';
import { persistor } from '../../store/ReduxStore';
import { GRN_STORAGE_KEYS } from '../sys/GrnConstants';
import {
  clearIndexedDB,
  clearCacheAPI,
  clearHistoryState
} from '../browser/browserCacheUtils';

/**
 * Clear any residual data from previous sessions
 * This should be called immediately after successful login
 */
export const initAfterLogin = async () => {
  try {
    // 1. Clear any GRN-related localStorage that might be from previous session
    const grnKeys = [
      GRN_STORAGE_KEYS.GRN_VIEW_DATA,
      GRN_STORAGE_KEYS.GRN_PAGE_DATA,
      GRN_STORAGE_KEYS.RETURNED_PRODUCTS,
      GRN_STORAGE_KEYS.BILLING_SUMMARY,
      GRN_STORAGE_KEYS.NEXT_PROCESS_RESPONSE,
    ];
    
    grnKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove ${key}:`, error);
      }
    });
    
    // 2. Clear any stale filters or grid states
    const staleKeys = [
      'filters',
      'gridFilters',
      'gridSort',
      'gridPagination',
      'dataGridState',
      'cart',
      'offlineDrafts',
    ];
    
    staleKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove stale key ${key}:`, error);
      }
    });
    
    // 3. Ensure Redux persist is ready
    await persistor.flush();
    
    // 4. Clear any React Query cache if present
    if (typeof window !== 'undefined' && window.__REACT_QUERY_CLIENT__) {
      try {
        const queryClient = window.__REACT_QUERY_CLIENT__;
        queryClient.clear();
      } catch (error) {
        console.debug('React Query not available or error clearing cache:', error);
      }
    }

    // 5. Clear IndexedDB databases
    try {
      const indexedDBResult = await clearIndexedDB();
      if (!indexedDBResult.success && indexedDBResult.errors.length > 0) {
        console.debug('IndexedDB clearing had errors:', indexedDBResult.errors);
      }
    } catch (error) {
      console.debug('IndexedDB clearing failed:', error);
    }

    // 6. Clear Cache API storage
    try {
      const cacheResult = await clearCacheAPI();
      if (!cacheResult.success && cacheResult.errors.length > 0) {
        console.debug('Cache API clearing had errors:', cacheResult.errors);
      }
    } catch (error) {
      console.debug('Cache API clearing failed:', error);
    }

    // 7. Clear Service Worker caches (but don't unregister workers)
    // Service Workers are part of the app, so we keep them but clear their caches
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        if (cacheNames.length > 0) {
          console.debug(`Cleared ${cacheNames.length} Service Worker cache(s)`);
        }
      }
    } catch (error) {
      console.debug('Service Worker cache clearing failed:', error);
    }

    // 8. Clear history state
    clearHistoryState();

    // 9. Reset any global state references
    if (typeof window !== 'undefined') {
      delete window.__USER_DATA__;
      delete window.__SESSION_DATA__;
    }

    console.log('Post-login initialization completed - browser cache cleared');
  } catch (error) {
    console.error('Error during post-login initialization:', error);
    // Don't throw - login should still proceed even if cleanup fails
  }
};

export default initAfterLogin;

